import React, { useState, useRef, useCallback, useEffect } from "react";
import { observer } from "mobx-react";
import { TextField, Select, Button } from "@ann/web-components";
import {
  FiltersContainerWrapper,
  FiltersColumn,
  SearchContainer,
} from "./FiltersContainer.styled";

const FiltersContainer = observer(
  ({
    loading,
    filters,
    filterHandlers,
    filterUI,
  }) => {
    const [localSearchValue, setLocalSearchValue] = useState(filters?.searchValue || "");
    const searchTimeoutRef = useRef(null);

    // Desestructurar valores de los objetos agrupados
    const {
      makeOptions,
      modelOptions,
      yearOptions,
      filteredMake,
      filteredModel,
      filteredYear,
      selectedMake,
      selectedModel,
      selectedYear,
      searchValue,
      hasActiveFilters,
    } = filters;

    const {
      handleMakeChange,
      handleModelChange,
      handleYearChange,
      handleSearchChange,
      handleClearFiltersWithKeys,
    } = filterHandlers;

    const {
      filtersKey,
      updateLocalSearch,
    } = filterUI;

    const handleSearchChangeCallback = useCallback((value) => {
      setLocalSearchValue(value);
      
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Set new timeout for 500ms debounce
      searchTimeoutRef.current = setTimeout(() => {
        if (handleSearchChange) {
          handleSearchChange(value);
        }
      }, 500);
    }, [handleSearchChange]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    // Sync local state with filters when it changes
    useEffect(() => {
      setLocalSearchValue(searchValue || "");
    }, [searchValue]);

    return (
      <FiltersContainerWrapper>
        <FiltersColumn>
          <SearchContainer>
            <TextField
              iconLeft="search"
              onChange={handleSearchChangeCallback}
              size="small"
              placeholder="Search"
              value={localSearchValue}
              disabled={loading}
            />
          </SearchContainer>
          
          <Select
            key={`make-filter-${filtersKey}`}
            label="Make"
            placeholder="Make"
            onChange={handleMakeChange}
            size="small"
            value={
              makeOptions.find((option) => option.value === selectedMake) || undefined
            }
            width="auto"
            minWidth="101px"
            disabled={loading}
          >
            <Select.Menu width="160px">
              <Select.Search
                placeholder="Search"
                onChange={(value) => updateLocalSearch("make", value)}
                label=""
              />
              {filteredMake.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          
          <Select
            key={`model-filter-${filtersKey}`}
            label="Model"
            placeholder="Model"
            onChange={handleModelChange}
            size="small"
            value={
              modelOptions.find((option) => option.value === selectedModel) || undefined
            }
            width="auto"
            minWidth="101px"
            disabled={loading || !makeOptions.length}
          >
            <Select.Menu width="160px">
              <Select.Search
                placeholder="Search"
                onChange={(value) => updateLocalSearch("model", value)}
                label=""
              />
              {filteredModel.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          
          <Select
            key={`year-filter-${filtersKey}`}
            label="Year"
            placeholder="Year"
            onChange={handleYearChange}
            size="small"
            value={
              yearOptions.find((option) => option.value === selectedYear) || undefined
            }
            width="auto"
            minWidth="90px"
            disabled={loading}
          >
            <Select.Menu width="160px">
              <Select.Search
                placeholder="Search"
                onChange={(value) => updateLocalSearch("year", value)}
                label=""
              />
              {filteredYear.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          
          {hasActiveFilters && (
            <Button
              icon=""
              iconLeft=""
              iconRight=""
              label="Clear Filters"
              size="medium"
              type="secondary"
              onClick={handleClearFiltersWithKeys}
              disabled={loading}
            />
          )}
        </FiltersColumn>
      </FiltersContainerWrapper>
    );
  }
);

export default FiltersContainer;

